import React, { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, IconVariant } from '@otp/ui';
import { User as UserIcon, UserAdd, UserRemove } from '@otp/icons';
import { RoleDropdown } from 'src/components/login-rules/form/roles/dropdown';
import { SelectedRole } from 'src/components/login-rules/form/types';
import { useRoles } from 'src/hooks/query/permissions/roles/use-roles';

type RolesSectionProps = {
  onChange: ({
    rolesIncluded,
    rolesExcluded,
  }: {
    rolesIncluded: SelectedRole[];
    rolesExcluded: SelectedRole[];
  }) => void;
  rolesIncluded: SelectedRole[];
  rolesExcluded: SelectedRole[];
};

export const RolesSection = ({
  onChange,
  rolesIncluded,
  rolesExcluded,
}: RolesSectionProps) => {
  const { t } = useTranslation();

  const { roles } = useRoles();

  const rolesOptions = useMemo(() => {
    if (!roles) {
      return [];
    }

    return roles.map((role) => ({
      name: role.name,
      value: role.id,
      iconVariant: IconVariant.AddIcon,
    }));
  }, [roles]);

  const rolesIncludedDropdownOptions = useMemo(() => {
    return rolesOptions.filter(
      (role) => !rolesIncluded.some((item) => item.id === role.value),
    );
  }, [rolesOptions, rolesIncluded]);

  const rolesExcludedDropdownOptions = useMemo(() => {
    return rolesOptions.filter(
      (role) => !rolesExcluded.some((item) => item.id === role.value),
    );
  }, [rolesOptions, rolesExcluded]);

  useEffect(() => {
    onChange({ rolesIncluded, rolesExcluded });
  }, [rolesIncluded, rolesExcluded]); // eslint-disable-next-line

  const addToRolesIncluded = (item: SelectedRole) => {
    onChange({ rolesIncluded: [...rolesIncluded, item], rolesExcluded });
  };

  const addToRolesExcluded = (item: SelectedRole) => {
    onChange({ rolesIncluded, rolesExcluded: [...rolesExcluded, item] });
  };

  const deleteFromRolesIncluded = (item: SelectedRole) => {
    onChange({
      rolesIncluded: rolesIncluded.filter((el) => el.id !== item.id),
      rolesExcluded,
    });
  };

  const deleteFromRolesExcluded = (item: SelectedRole) => {
    onChange({
      rolesIncluded,
      rolesExcluded: rolesExcluded.filter((el) => el.id !== item.id),
    });
  };

  return (
    <div className="">
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        color="primary.10"
        className="flex items-center uppercase"
      >
        <UserIcon className="mr-3" />
        {t('login-rules.form.permission-role.title')}
      </Typography>

      <div className="flex flex-row space-x-8 mt-3">
        <div className="w-full">
          <div className="flex flex-row items-center">
            <UserAdd />
            <Typography variant="subtitle1" className="pl-2">
              {t('login-rules.form.permission-role.add-select-title')}
            </Typography>
          </div>

          <RoleDropdown
            onAddItem={addToRolesIncluded}
            onRemoveItem={deleteFromRolesIncluded}
            options={rolesIncludedDropdownOptions}
            selectedItems={rolesIncluded}
            listName={t('login-rules.form.permission-role.added-list-title')}
            listNameClass="bg-blue-300"
          />
        </div>

        <div className="w-full">
          <div className="flex flex-row items-center">
            <UserRemove />
            <Typography variant="subtitle1" className="pl-2">
              {t('login-rules.form.permission-role.remove-select-title')}
            </Typography>
          </div>

          <RoleDropdown
            onAddItem={addToRolesExcluded}
            onRemoveItem={deleteFromRolesExcluded}
            options={rolesExcludedDropdownOptions}
            selectedItems={rolesExcluded}
            listName={t('login-rules.form.permission-role.removed-list-title')}
            listNameClass="bg-red-400"
          />
        </div>
      </div>
    </div>
  );
};
